import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock3,
  Copy,
  Download,
  EyeOff,
  Globe2,
  Loader2,
  Mail,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  ShieldAlert,
  Sparkles
} from 'lucide-react';
import { api } from '../utils/api';
import './MarketplaceManagement.css';

const DEFAULT_CONFIG = {
  enabled: true,
  publicUrl: '',
  featuredMerchantsEnabled: true,
  sponsoredProducts: {
    enabled: true,
    slots: 20,
    rotationHours: 6,
    queuePolicy: 'FIFO',
    organicFallbackEnabled: false,
    reportNote: 'Boost improves visibility, not guaranteed sales.',
    defaultPackage: {
      name: 'Marketplace Boost',
      price: 7500,
      durationDays: 7
    },
    bundleDiscount: {
      enabled: true,
      minProducts: 3,
      discountPercent: 10
    },
    tierPricing: {
      FREE: { discountPercent: 0, priorityWeight: 1 },
      PRO: { discountPercent: 10, priorityWeight: 1.1 },
      PREMIUM: { discountPercent: 20, priorityWeight: 1.25 }
    }
  }
};

const formatCurrency = (value) => `₦${Number(value || 0).toLocaleString()}`;

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-NG', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getAdminApiUrl = (endpoint) => {
  const base = process.env.REACT_APP_API_URL;
  if (!base) return `/api${endpoint}`;
  const clean = base.endsWith('/') ? base.slice(0, -1) : base;
  return clean.endsWith('/api') ? `${clean}${endpoint}` : `${clean}/api${endpoint}`;
};

function MarketplaceManagement() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [campaigns, setCampaigns] = useState([]);
  const [campaignTotals, setCampaignTotals] = useState({ spend: 0, impressions: 0, clicks: 0, productViews: 0, ctr: 0 });
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState('');
  const [sendingReportId, setSendingReportId] = useState('');
  const [cancellingId, setCancellingId] = useState('');
  const [notingId, setNotingId] = useState('');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableStores, setAvailableStores] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Moderation state
  const [moderationProducts, setModerationProducts] = useState([]);
  const [moderationTotal, setModerationTotal] = useState(0);
  const [moderationLoading, setModerationLoading] = useState(false);
  const [moderationQuery, setModerationQuery] = useState('');
  const [moderationSearchInput, setModerationSearchInput] = useState('');
  const [moderationStatus, setModerationStatus] = useState('');
  const [moderationPage, setModerationPage] = useState(1);
  const [moderationPageCount, setModerationPageCount] = useState(1);
  const [moderationActionId, setModerationActionId] = useState('');
  const [hideModal, setHideModal] = useState({ open: false, productId: '', productName: '', reason: '' });

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/marketplace/config');
      setConfig(response.config || DEFAULT_CONFIG);
    } catch (err) {
      console.error('[MarketplaceManagement] Failed to load config:', err);
      setError(err.message || 'Failed to load marketplace configuration.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    api.get('/admin/marketplace/categories/available')
      .then((response) => setAvailableCategories(response.categories || []))
      .catch((err) => console.error('[MarketplaceManagement] Failed to load categories:', err));
    api.get('/admin/marketplace/stores/available')
      .then((response) => setAvailableStores(response.stores || []))
      .catch((err) => console.error('[MarketplaceManagement] Failed to load stores:', err));
  }, []);

  const loadCampaigns = async () => {
    try {
      setCampaignsLoading(true);
      setError('');
      const query = campaignStatus ? `?status=${encodeURIComponent(campaignStatus)}` : '';
      const response = await api.get(`/admin/marketplace/boosts/campaigns${query}`);
      setCampaigns(response.campaigns || []);
      setCampaignTotals(response.totals || { spend: 0, impressions: 0, clicks: 0, productViews: 0, ctr: 0 });
    } catch (err) {
      console.error('[MarketplaceManagement] Failed to load boost campaigns:', err);
      setError(err.message || 'Failed to load boost campaigns.');
    } finally {
      setCampaignsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'boosts') {
      loadCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, campaignStatus]);

  const loadModerationProducts = async () => {
    try {
      setModerationLoading(true);
      setError('');
      const params = new URLSearchParams({
        page: String(moderationPage),
        limit: '20'
      });
      if (moderationQuery) params.set('q', moderationQuery);
      if (moderationStatus) params.set('status', moderationStatus);
      const response = await api.get(`/admin/marketplace/products?${params.toString()}`);
      setModerationProducts(response.products || []);
      setModerationTotal(Number(response.total || 0));
      setModerationPageCount(Number(response.totalPages || 1));
    } catch (err) {
      console.error('[MarketplaceManagement] Failed to load moderation products:', err);
      setError(err.message || 'Failed to load products.');
    } finally {
      setModerationLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'moderation') {
      loadModerationProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, moderationPage, moderationQuery, moderationStatus]);

  const submitModerationSearch = (event) => {
    event.preventDefault();
    setModerationPage(1);
    setModerationQuery(moderationSearchInput.trim());
  };

  const openHideModal = (product) => {
    setHideModal({
      open: true,
      productId: product.id,
      productName: product.name,
      reason: ''
    });
  };

  const closeHideModal = () => setHideModal({ open: false, productId: '', productName: '', reason: '' });

  const confirmHide = async () => {
    if (!hideModal.productId || !hideModal.reason.trim()) return;
    try {
      setModerationActionId(hideModal.productId);
      await api.post(`/admin/marketplace/products/${hideModal.productId}/moderate`, {
        marketplaceStatus: 'HIDDEN',
        reason: hideModal.reason.trim()
      });
      setSuccess('Product hidden from marketplace.');
      setTimeout(() => setSuccess(''), 2400);
      closeHideModal();
      await loadModerationProducts();
    } catch (err) {
      setError(err.message || 'Failed to hide product.');
    } finally {
      setModerationActionId('');
    }
  };

  const restoreProduct = async (product) => {
    if (!product?.id) return;
    try {
      setModerationActionId(product.id);
      await api.post(`/admin/marketplace/products/${product.id}/moderate`, {
        marketplaceStatus: 'APPROVED'
      });
      setSuccess('Product restored on marketplace.');
      setTimeout(() => setSuccess(''), 2400);
      await loadModerationProducts();
    } catch (err) {
      setError(err.message || 'Failed to restore product.');
    } finally {
      setModerationActionId('');
    }
  };

  const updateConfig = (updater) => {
    setConfig((current) => updater(current));
  };

  const updateSponsored = (field, value) => {
    updateConfig((current) => ({
      ...current,
      sponsoredProducts: {
        ...current.sponsoredProducts,
        [field]: value
      }
    }));
  };

  const updatePackage = (field, value) => {
    updateConfig((current) => ({
      ...current,
      sponsoredProducts: {
        ...current.sponsoredProducts,
        defaultPackage: {
          ...current.sponsoredProducts.defaultPackage,
          [field]: value
        }
      }
    }));
  };

  const addCategorySection = () => {
    updateConfig((current) => {
      const existing = Array.isArray(current.categorySections) ? current.categorySections : [];
      if (existing.length >= 5) return current;
      return {
        ...current,
        categorySections: [...existing, { category: '', title: '' }]
      };
    });
  };

  const updateCategorySection = (index, field, value) => {
    updateConfig((current) => {
      const existing = Array.isArray(current.categorySections) ? [...current.categorySections] : [];
      if (!existing[index]) return current;
      existing[index] = { ...existing[index], [field]: value };
      if (field === 'category' && (!existing[index].title || !existing[index].title.trim())) {
        existing[index].title = value;
      }
      return { ...current, categorySections: existing };
    });
  };

  const removeCategorySection = (index) => {
    updateConfig((current) => {
      const existing = Array.isArray(current.categorySections) ? [...current.categorySections] : [];
      existing.splice(index, 1);
      return { ...current, categorySections: existing };
    });
  };

  const addPicksFallbackEntry = () => {
    updateConfig((current) => {
      const existing = Array.isArray(current.marketplacePicksFallback) ? current.marketplacePicksFallback : [];
      if (existing.length >= 10) return current;
      return {
        ...current,
        marketplacePicksFallback: [...existing, { storeSlug: '', productLimit: 3 }]
      };
    });
  };

  const updatePicksFallbackEntry = (index, field, value) => {
    updateConfig((current) => {
      const existing = Array.isArray(current.marketplacePicksFallback) ? [...current.marketplacePicksFallback] : [];
      if (!existing[index]) return current;
      existing[index] = { ...existing[index], [field]: value };
      return { ...current, marketplacePicksFallback: existing };
    });
  };

  const removePicksFallbackEntry = (index) => {
    updateConfig((current) => {
      const existing = Array.isArray(current.marketplacePicksFallback) ? [...current.marketplacePicksFallback] : [];
      existing.splice(index, 1);
      return { ...current, marketplacePicksFallback: existing };
    });
  };

  const updateBundle = (field, value) => {
    updateConfig((current) => ({
      ...current,
      sponsoredProducts: {
        ...current.sponsoredProducts,
        bundleDiscount: {
          ...(current.sponsoredProducts.bundleDiscount || DEFAULT_CONFIG.sponsoredProducts.bundleDiscount),
          [field]: value
        }
      }
    }));
  };

  const updateTier = (tier, field, value) => {
    updateConfig((current) => ({
      ...current,
      sponsoredProducts: {
        ...current.sponsoredProducts,
        tierPricing: {
          ...current.sponsoredProducts.tierPricing,
          [tier]: {
            ...current.sponsoredProducts.tierPricing[tier],
            [field]: value
          }
        }
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const response = await api.put('/admin/marketplace/config', config);
      setConfig(response.config || config);
      setSuccess(response.message || 'Marketplace configuration updated.');
    } catch (err) {
      console.error('[MarketplaceManagement] Failed to save config:', err);
      setError(err.message || 'Failed to save marketplace configuration.');
    } finally {
      setSaving(false);
    }
  };

  const copyPublicUrl = async () => {
    if (!config.publicUrl) return;
    try {
      await navigator.clipboard.writeText(config.publicUrl);
      setSuccess('Marketplace URL copied.');
      setTimeout(() => setSuccess(''), 1800);
    } catch (err) {
      setError('Failed to copy marketplace URL.');
    }
  };

  const downloadBoostCsv = async () => {
    try {
      setError('');
      const query = campaignStatus ? `?status=${encodeURIComponent(campaignStatus)}` : '';
      const response = await fetch(getAdminApiUrl(`/admin/marketplace/boosts/campaigns.csv${query}`), {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to download boost campaigns.');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `marketplace-boost-campaigns-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download boost campaigns.');
    }
  };

  const sendMerchantReport = async (campaign) => {
    if (!campaign?.merchantId || !campaign?.merchantEmail) return;
    try {
      setSendingReportId(campaign.id);
      setError('');
      setSuccess('');
      const response = await api.post('/admin/marketplace/boosts/send-report', {
        merchantId: campaign.merchantId,
        recipientEmail: campaign.merchantEmail
      });
      setSuccess(response.message || `Report sent to ${campaign.merchantEmail}.`);
    } catch (err) {
      console.error('[MarketplaceManagement] Failed to send boost report:', err);
      setError(err.message || 'Failed to send marketplace boost report.');
    } finally {
      setSendingReportId('');
    }
  };

  const cancelCampaign = async (campaign) => {
    if (!campaign?.id) return;
    if (['CANCELLED', 'COMPLETED'].includes(campaign.status)) {
      setError(`Campaign is already ${campaign.status}.`);
      return;
    }
    // Simple prompt for reason — deliberate friction so it isn't clicked
    // by accident. Full confirm-modal treatment can come later.
    const reason = window.prompt(
      `Cancel this ${campaign.packageName} boost for "${campaign.productName || 'product'}"? Reason (required, will be logged):`
    );
    if (!reason || reason.trim().length < 3) return;
    try {
      setCancellingId(campaign.id);
      setError('');
      setSuccess('');
      const res = await api.post(`/admin/marketplace/boosts/campaigns/${campaign.id}/cancel`, {
        reason: reason.trim(),
      });
      setSuccess(`Campaign cancelled. ${res?.data ? '' : ''}`);
      await loadCampaigns();
    } catch (err) {
      console.error('[MarketplaceManagement] cancel error:', err);
      setError(err.message || 'Failed to cancel campaign.');
    } finally {
      setCancellingId('');
    }
  };

  const addAdminNote = async (campaign) => {
    if (!campaign?.id) return;
    const note = window.prompt('Admin note for this campaign (visible only to platform staff):');
    if (!note || note.trim().length < 1) return;
    try {
      setNotingId(campaign.id);
      setError('');
      setSuccess('');
      await api.post(`/admin/marketplace/boosts/campaigns/${campaign.id}/notes`, { note: note.trim() });
      setSuccess('Note added.');
      await loadCampaigns();
    } catch (err) {
      console.error('[MarketplaceManagement] add note error:', err);
      setError(err.message || 'Failed to add note.');
    } finally {
      setNotingId('');
    }
  };

  if (loading) {
    return (
      <div className="mpm-page">
        <div className="mpm-loading">
          <Loader2 size={28} className="spin" />
          <span>Loading marketplace configuration…</span>
        </div>
      </div>
    );
  }

  const pkg = config.sponsoredProducts.defaultPackage;
  const bundle = config.sponsoredProducts.bundleDiscount || DEFAULT_CONFIG.sponsoredProducts.bundleDiscount;
  const tiers = config.sponsoredProducts.tierPricing || {};

  return (
    <div className="mpm-page">
      <header className="mpm-header">
        <div>
          <span className="mpm-eyebrow">Platform Management</span>
          <h1>Marketplace</h1>
          <p>Control the public marketplace host, sponsored placement rules, pricing, and merchant-facing reporting language.</p>
        </div>
        <div className="mpm-header-actions">
          <button type="button" className="mpm-btn mpm-btn-secondary" onClick={loadConfig}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button type="button" className="mpm-btn mpm-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
            Save changes
          </button>
        </div>
      </header>

      {error ? (
        <div className="mpm-banner mpm-banner-error">
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      ) : null}

      {success ? (
        <div className="mpm-banner mpm-banner-success">
          <CheckCircle2 size={16} />
          <span>{success}</span>
        </div>
      ) : null}

      <nav className="mpm-tabs" aria-label="Marketplace admin sections">
        {[
          { id: 'settings', label: 'Settings', icon: Globe2 },
          { id: 'boosts', label: 'Boost Activity', icon: BarChart3 },
          { id: 'moderation', label: 'Moderation', icon: ShieldAlert }
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              className={`mpm-tab${activeTab === tab.id ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {activeTab === 'boosts' ? (
        <>
          <section className="mpm-stats-grid">
            <article className="mpm-stat-card">
              <span>Total paid</span>
              <strong>{formatCurrency(campaignTotals.spend)}</strong>
            </article>
            <article className="mpm-stat-card">
              <span>Impressions</span>
              <strong>{Number(campaignTotals.impressions || 0).toLocaleString()}</strong>
            </article>
            <article className="mpm-stat-card">
              <span>Clicks</span>
              <strong>{Number(campaignTotals.clicks || 0).toLocaleString()}</strong>
            </article>
            <article className="mpm-stat-card">
              <span>CTR</span>
              <strong>{campaignTotals.ctr || 0}%</strong>
            </article>
          </section>

          <section className="mpm-card">
            <div className="mpm-card-head mpm-card-head-actions">
              <div>
                <span className="mpm-label">Sponsored operations</span>
                <h2>Boost payments and queue</h2>
              </div>
              <div className="mpm-table-actions">
                <select
                  className="mpm-filter"
                  value={campaignStatus}
                  onChange={(e) => setCampaignStatus(e.target.value)}
                >
                  <option value="">All statuses</option>
                  <option value="QUEUED">Queued</option>
                  <option value="ACTIVE">Active</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
                <button type="button" className="mpm-btn mpm-btn-secondary" onClick={loadCampaigns}>
                  <RefreshCw size={16} className={campaignsLoading ? 'spin' : ''} />
                  Refresh
                </button>
                <button type="button" className="mpm-btn mpm-btn-secondary" onClick={downloadBoostCsv}>
                  <Download size={16} />
                  CSV
                </button>
              </div>
            </div>

            {campaignsLoading ? (
              <div className="mpm-loading mpm-loading-compact">
                <Loader2 size={22} className="spin" />
                <span>Loading boost campaigns…</span>
              </div>
            ) : campaigns.length ? (
              <div className="mpm-table-wrap">
                <table className="mpm-table">
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      <th>Product</th>
                      <th>Payment</th>
                      <th>Queue</th>
                      <th>Run</th>
                      <th>Metrics</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((campaign) => (
                      <tr key={campaign.id}>
                        <td>
                          <strong>{campaign.storeName || campaign.merchantName || 'Merchant'}</strong>
                          <small>{campaign.merchantEmail}</small>
                        </td>
                        <td>
                          <strong>{campaign.productName || 'Product'}</strong>
                          <small>{campaign.packageName}</small>
                        </td>
                        <td>
                          <span className={`mpm-status mpm-status-${String(campaign.paymentStatus || 'PENDING').toLowerCase()}`}>
                            {campaign.paymentStatus || 'PENDING'}
                          </span>
                          <strong>{formatCurrency(campaign.price)}</strong>
                          <small>{campaign.checkoutReference || 'No reference'}</small>
                        </td>
                        <td>
                          <span className={`mpm-status mpm-status-${String(campaign.status || '').toLowerCase()}`}>
                            {campaign.status}
                          </span>
                          <small>Weight {campaign.priorityWeight} · Position {campaign.queuePosition || '—'}</small>
                          <small>{campaign.merchantTier} · {campaign.discountPercent || 0}% discount</small>
                        </td>
                        <td>
                          <span className="mpm-run-line"><Clock3 size={13} /> {campaign.durationDays} days</span>
                          <small>Queued {formatDate(campaign.queuedAt)}</small>
                          <small>Start {formatDate(campaign.startAt)}</small>
                        </td>
                        <td>
                          <strong>{Number(campaign.impressions || 0).toLocaleString()} views</strong>
                          <small>{Number(campaign.clicks || 0).toLocaleString()} clicks · {Number(campaign.productViews || 0).toLocaleString()} opens</small>
                          <small>{campaign.ctr || 0}% CTR</small>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                            <button
                              type="button"
                              className="mpm-icon-text-btn"
                              onClick={() => sendMerchantReport(campaign)}
                              disabled={sendingReportId === campaign.id || !campaign.merchantEmail}
                              title="Email the merchant a campaign report"
                            >
                              {sendingReportId === campaign.id ? <Loader2 size={14} className="spin" /> : <Mail size={14} />}
                              Report
                            </button>
                            <button
                              type="button"
                              className="mpm-icon-text-btn"
                              onClick={() => addAdminNote(campaign)}
                              disabled={notingId === campaign.id}
                              title="Add an internal note (visible to platform staff only)"
                              style={{ color: '#4b5563' }}
                            >
                              {notingId === campaign.id ? <Loader2 size={14} className="spin" /> : '📝'}
                              Note{campaign.adminNotes ? ` (${campaign.adminNotes.split('\n').length})` : ''}
                            </button>
                            {['QUEUED', 'ACTIVE'].includes(campaign.status) && (
                              <button
                                type="button"
                                className="mpm-icon-text-btn"
                                onClick={() => cancelCampaign(campaign)}
                                disabled={cancellingId === campaign.id}
                                title="Force-cancel this campaign (reason required)"
                                style={{ color: '#dc2626' }}
                              >
                                {cancellingId === campaign.id ? <Loader2 size={14} className="spin" /> : '✕'}
                                Cancel
                              </button>
                            )}
                            {campaign.cancelledReason && (
                              <small style={{ color: '#6b7280', fontStyle: 'italic', maxWidth: 200 }}>
                                {campaign.cancelledReason}
                              </small>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mpm-empty-state">
                <Sparkles size={22} />
                <strong>No boost campaigns yet</strong>
                <p>Paid merchant boosts will appear here with queue position, weight, payment reference, and campaign metrics.</p>
              </div>
            )}
          </section>
        </>
      ) : activeTab === 'moderation' ? (
        <>
          <section className="mpm-card">
            <div className="mpm-card-head">
              <div>
                <span className="mpm-label">Product moderation</span>
                <h2>Hide problem products from the marketplace</h2>
              </div>
            </div>
            <p className="mpm-inline-note" style={{ marginTop: 0 }}>
              Hidden products stay live on the merchant's own storefront but stop appearing on marketplace.userevstar.com and stop being eligible for boost purchases. Use for illegal items, counterfeits, adult content, spam, or clear policy violations.
            </p>

            <form className="mpm-form-grid" style={{ alignItems: 'end', marginTop: 12 }} onSubmit={submitModerationSearch}>
              <label className="mpm-field mpm-field-span">
                <span>Search products</span>
                <input
                  type="search"
                  value={moderationSearchInput}
                  onChange={(e) => setModerationSearchInput(e.target.value)}
                  placeholder="Name, SKU, category, or brand…"
                />
              </label>
              <label className="mpm-field">
                <span>Status</span>
                <select
                  value={moderationStatus}
                  onChange={(e) => {
                    setModerationPage(1);
                    setModerationStatus(e.target.value);
                  }}
                >
                  <option value="">All statuses</option>
                  <option value="APPROVED">Approved (visible)</option>
                  <option value="HIDDEN">Hidden</option>
                  <option value="PENDING">Pending review</option>
                </select>
              </label>
              <button type="submit" className="mpm-btn mpm-btn-secondary">
                <Search size={14} /> Search
              </button>
            </form>

            <div style={{ marginTop: 16, color: '#64748b', fontSize: 13 }}>
              {moderationLoading ? 'Loading…' : `${moderationTotal} product${moderationTotal === 1 ? '' : 's'} found`}
            </div>

            {moderationLoading ? (
              <div className="mpm-empty-state" style={{ marginTop: 12 }}>
                <Loader2 size={18} className="mpm-spin" />
                <strong>Loading products…</strong>
              </div>
            ) : moderationProducts.length ? (
              <div className="mpm-table-wrap" style={{ marginTop: 12 }}>
                <table className="mpm-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Merchant</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {moderationProducts.map((product) => {
                      const isHidden = product.marketplaceStatus === 'HIDDEN';
                      const busy = moderationActionId === product.id;
                      return (
                        <tr key={product.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              {product.primaryImage ? (
                                <img
                                  src={product.primaryImage}
                                  alt=""
                                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, background: '#f3f4f6' }}
                                />
                              ) : (
                                <div style={{ width: 40, height: 40, borderRadius: 6, background: '#f3f4f6' }} />
                              )}
                              <div>
                                <strong style={{ display: 'block', color: '#111827' }}>{product.name}</strong>
                                {isHidden && product.marketplaceHiddenReason ? (
                                  <small style={{ color: '#b91c1c' }}>Reason: {product.marketplaceHiddenReason}</small>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>{product.merchantName || '—'}</div>
                            {product.merchantSlug ? (
                              <small style={{ color: '#64748b' }}>{product.merchantSlug}</small>
                            ) : null}
                          </td>
                          <td>{product.category || '—'}</td>
                          <td>{formatCurrency(product.price)}</td>
                          <td>
                            <span
                              className={`mpm-status-pill mpm-status-pill--${product.marketplaceStatus.toLowerCase()}`}
                            >
                              {product.marketplaceStatus}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {isHidden ? (
                              <button
                                type="button"
                                className="mpm-btn mpm-btn-secondary"
                                onClick={() => restoreProduct(product)}
                                disabled={busy}
                              >
                                <RotateCcw size={14} /> Restore
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="mpm-btn mpm-btn-danger"
                                onClick={() => openHideModal(product)}
                                disabled={busy}
                              >
                                <EyeOff size={14} /> Hide
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mpm-empty-state" style={{ marginTop: 12 }}>
                <ShieldAlert size={22} />
                <strong>No products match</strong>
                <p>Try a different search term or clear the status filter.</p>
              </div>
            )}

            {moderationPageCount > 1 ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
                <button
                  type="button"
                  className="mpm-btn mpm-btn-secondary"
                  disabled={moderationPage <= 1 || moderationLoading}
                  onClick={() => setModerationPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span style={{ color: '#64748b', fontSize: 13 }}>
                  Page {moderationPage} of {moderationPageCount}
                </span>
                <button
                  type="button"
                  className="mpm-btn mpm-btn-secondary"
                  disabled={moderationPage >= moderationPageCount || moderationLoading}
                  onClick={() => setModerationPage((p) => Math.min(moderationPageCount, p + 1))}
                >
                  Next
                </button>
              </div>
            ) : null}
          </section>

          {hideModal.open ? (
            <div
              className="mpm-modal-overlay"
              onClick={closeHideModal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="hide-product-title"
            >
              <div className="mpm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mpm-card-head">
                  <div>
                    <span className="mpm-label">Hide from marketplace</span>
                    <h3 id="hide-product-title" style={{ margin: 0 }}>{hideModal.productName}</h3>
                  </div>
                </div>
                <p className="mpm-inline-note" style={{ marginTop: 0 }}>
                  The merchant's own storefront will keep serving this product — only marketplace visibility is affected. Give a short reason so the audit trail is clear.
                </p>
                <label className="mpm-field" style={{ marginTop: 12 }}>
                  <span>Reason</span>
                  <textarea
                    value={hideModal.reason}
                    onChange={(e) => setHideModal((prev) => ({ ...prev, reason: e.target.value }))}
                    placeholder="e.g. Illegal item, counterfeit brand, adult content, spam listing"
                    rows={3}
                    style={{ resize: 'vertical' }}
                  />
                </label>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button type="button" className="mpm-btn mpm-btn-secondary" onClick={closeHideModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="mpm-btn mpm-btn-danger"
                    onClick={confirmHide}
                    disabled={!hideModal.reason.trim() || moderationActionId === hideModal.productId}
                  >
                    {moderationActionId === hideModal.productId ? 'Hiding…' : 'Hide product'}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <>
      <section className="mpm-grid">
        <article className="mpm-card mpm-card-hero">
          <div className="mpm-card-head">
            <div>
              <span className="mpm-label">Customer entry point</span>
              <h2>Public marketplace host</h2>
            </div>
            <Globe2 size={20} />
          </div>
          <div className="mpm-public-url">
            <code>{config.publicUrl || 'Not available'}</code>
            <button type="button" className="mpm-icon-btn" onClick={copyPublicUrl} title="Copy URL">
              <Copy size={15} />
            </button>
          </div>
          <p className="mpm-muted">
            Customers should enter the marketplace through this public host. The merchant dashboard only manages campaigns and settings.
          </p>
        </article>

        <article className="mpm-card">
          <div className="mpm-card-head">
            <div>
              <span className="mpm-label">Availability</span>
              <h2>Core switches</h2>
            </div>
            <Sparkles size={20} />
          </div>
          <label className="mpm-switch-row">
            <div>
              <strong>Marketplace enabled</strong>
              <small>Controls whether the public marketplace should be considered live platform-wide.</small>
            </div>
            <input
              type="checkbox"
              checked={!!config.enabled}
              onChange={(e) => updateConfig((current) => ({ ...current, enabled: e.target.checked }))}
            />
          </label>
          <label className="mpm-switch-row">
            <div>
              <strong>Featured merchant discovery</strong>
              <small>Allow the public marketplace to show merchant/store discovery alongside paid product slots.</small>
            </div>
            <input
              type="checkbox"
              checked={!!config.featuredMerchantsEnabled}
              onChange={(e) => updateConfig((current) => ({ ...current, featuredMerchantsEnabled: e.target.checked }))}
            />
          </label>
          <label className="mpm-switch-row">
            <div>
              <strong>Sponsored products enabled</strong>
              <small>Turns paid product exposure on or off without deleting package settings.</small>
            </div>
            <input
              type="checkbox"
              checked={!!config.sponsoredProducts.enabled}
              onChange={(e) => updateSponsored('enabled', e.target.checked)}
            />
          </label>
        </article>
      </section>

      <section className="mpm-grid">
        <article className="mpm-card">
          <div className="mpm-card-head">
            <div>
              <span className="mpm-label">Sponsored inventory</span>
              <h2>Rotation and queue</h2>
            </div>
          </div>
          <div className="mpm-form-grid">
            <label className="mpm-field">
              <span>Visible slots</span>
              <input
                type="number"
                min="1"
                value={config.sponsoredProducts.slots}
                onChange={(e) => updateSponsored('slots', Number(e.target.value))}
              />
            </label>
            <label className="mpm-field">
              <span>Rotation window (hours)</span>
              <input
                type="number"
                min="1"
                value={config.sponsoredProducts.rotationHours}
                onChange={(e) => updateSponsored('rotationHours', Number(e.target.value))}
              />
            </label>
            <label className="mpm-field">
              <span>Queue policy</span>
              <select
                value={config.sponsoredProducts.queuePolicy}
                onChange={(e) => updateSponsored('queuePolicy', e.target.value)}
              >
                <option value="FIFO">FIFO</option>
                <option value="ROUND_ROBIN">Round Robin</option>
                <option value="PRIORITY_WEIGHTED">Priority Weighted</option>
              </select>
            </label>
          </div>
          <label className="mpm-switch-row mpm-switch-row-border">
            <div>
              <strong>Organic fallback</strong>
              <small>Use non-sponsored products when sponsored inventory is empty.</small>
            </div>
            <input
              type="checkbox"
              checked={!!config.sponsoredProducts.organicFallbackEnabled}
              onChange={(e) => updateSponsored('organicFallbackEnabled', e.target.checked)}
            />
          </label>
          <label className="mpm-field">
            <span>Merchant report note</span>
            <textarea
              rows="3"
              value={config.sponsoredProducts.reportNote}
              onChange={(e) => updateSponsored('reportNote', e.target.value)}
              placeholder="Boost improves visibility, not guaranteed sales."
            />
          </label>
        </article>

        <article className="mpm-card">
          <div className="mpm-card-head">
            <div>
              <span className="mpm-label">Default package</span>
              <h2>Boost pricing</h2>
            </div>
          </div>
          <div className="mpm-form-grid">
            <label className="mpm-field mpm-field-span">
              <span>Package name</span>
              <input
                type="text"
                value={pkg.name}
                onChange={(e) => updatePackage('name', e.target.value)}
              />
            </label>
            <label className="mpm-field">
              <span>Price (NGN)</span>
              <input
                type="number"
                min="0"
                value={pkg.price}
                onChange={(e) => updatePackage('price', Number(e.target.value))}
              />
            </label>
            <label className="mpm-field">
              <span>Duration (days)</span>
              <input
                type="number"
                min="1"
                value={pkg.durationDays}
                onChange={(e) => updatePackage('durationDays', Number(e.target.value))}
              />
            </label>
          </div>
          <div className="mpm-inline-note">
            Merchant-facing final price is this base package less the tier discount below.
          </div>
        </article>
      </section>

      <section className="mpm-card">
        <div className="mpm-card-head">
          <div>
            <span className="mpm-label">Multi-product incentive</span>
            <h2>Bundle discount</h2>
          </div>
        </div>
        <label className="mpm-toggle">
          <input
            type="checkbox"
            checked={!!bundle.enabled}
            onChange={(e) => updateBundle('enabled', e.target.checked)}
          />
          <span>Give merchants a discount when they boost multiple products in one checkout</span>
        </label>
        <div className="mpm-form-grid">
          <label className="mpm-field">
            <span>Minimum products</span>
            <input
              type="number"
              min="2"
              value={bundle.minProducts}
              disabled={!bundle.enabled}
              onChange={(e) => updateBundle('minProducts', Number(e.target.value))}
            />
          </label>
          <label className="mpm-field">
            <span>Discount %</span>
            <input
              type="number"
              min="0"
              max="90"
              value={bundle.discountPercent}
              disabled={!bundle.enabled}
              onChange={(e) => updateBundle('discountPercent', Number(e.target.value))}
            />
          </label>
        </div>
        <div className="mpm-inline-note">
          Applied to the subtotal after each product's tier discount, when the merchant boosts at least this many products in a single checkout.
        </div>
      </section>

      <section className="mpm-card">
        <div className="mpm-card-head">
          <div>
            <span className="mpm-label">Marketplace discovery</span>
            <h2>Category rows</h2>
          </div>
        </div>
        <p className="mpm-inline-note" style={{ marginTop: 0 }}>
          Pick up to 5 categories to feature as horizontal-scroll rows on the marketplace home. Each row shows 8 products with boosted campaigns surfaced first.
        </p>
        {(config.categorySections || []).map((section, index) => (
          <div key={index} className="mpm-form-grid" style={{ alignItems: 'end', marginTop: 12 }}>
            <label className="mpm-field">
              <span>Category</span>
              <select
                value={section.category || ''}
                onChange={(e) => updateCategorySection(index, 'category', e.target.value)}
              >
                <option value="">Choose a category…</option>
                {availableCategories.map((row) => (
                  <option key={row.category} value={row.category}>
                    {row.category} ({row.productCount})
                  </option>
                ))}
                {section.category && !availableCategories.some((row) => row.category === section.category) ? (
                  <option value={section.category}>{section.category} (custom)</option>
                ) : null}
              </select>
            </label>
            <label className="mpm-field">
              <span>Display title</span>
              <input
                type="text"
                value={section.title || ''}
                onChange={(e) => updateCategorySection(index, 'title', e.target.value)}
                placeholder={section.category || 'Row title'}
              />
            </label>
            <button
              type="button"
              className="mpm-btn mpm-btn-secondary"
              onClick={() => removeCategorySection(index)}
            >
              Remove
            </button>
          </div>
        ))}
        {(config.categorySections || []).length < 5 ? (
          <button
            type="button"
            className="mpm-btn mpm-btn-secondary"
            style={{ marginTop: 12 }}
            onClick={addCategorySection}
          >
            + Add category row
          </button>
        ) : (
          <div className="mpm-inline-note">Maximum of 5 category rows reached.</div>
        )}
      </section>

      <section className="mpm-card">
        <div className="mpm-card-head">
          <div>
            <span className="mpm-label">Marketplace picks fallback</span>
            <h2>Curated stores when no boosts are running</h2>
          </div>
        </div>
        <p className="mpm-inline-note" style={{ marginTop: 0 }}>
          When zero sponsored products are active, the "Marketplace picks" section falls back to this curated list — pull N of the latest / featured products from each selected store. Sponsored placements always win when they exist; this only activates as an inventory fallback. If the picks come short, the section is topped up automatically from organic products.
        </p>
        {(() => {
          const rows = Array.isArray(config.marketplacePicksFallback) ? config.marketplacePicksFallback : [];
          const total = rows.reduce((sum, row) => sum + Math.max(1, Number(row.productLimit) || 0), 0);
          return (
            <>
              {rows.map((entry, index) => (
                <div key={index} className="mpm-form-grid" style={{ alignItems: 'end', marginTop: 12 }}>
                  <label className="mpm-field">
                    <span>Store</span>
                    <select
                      value={entry.storeSlug || ''}
                      onChange={(e) => updatePicksFallbackEntry(index, 'storeSlug', e.target.value)}
                    >
                      <option value="">Choose a store…</option>
                      {availableStores.map((store) => (
                        <option key={store.slug} value={store.slug}>
                          {store.name} ({store.productCount})
                        </option>
                      ))}
                      {entry.storeSlug && !availableStores.some((s) => s.slug === entry.storeSlug) ? (
                        <option value={entry.storeSlug}>{entry.storeSlug} (offline)</option>
                      ) : null}
                    </select>
                  </label>
                  <label className="mpm-field">
                    <span>Products to show</span>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={entry.productLimit}
                      onChange={(e) => updatePicksFallbackEntry(index, 'productLimit', Number(e.target.value))}
                    />
                  </label>
                  <button
                    type="button"
                    className="mpm-btn mpm-btn-secondary"
                    onClick={() => removePicksFallbackEntry(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {total > 20 ? (
                <div className="mpm-banner mpm-banner-warning" style={{ marginTop: 12 }}>
                  <AlertTriangle size={16} />
                  <span>{total} products across all rows — consider trimming below 20 so the section stays scannable.</span>
                </div>
              ) : null}
              {rows.length < 10 ? (
                <button
                  type="button"
                  className="mpm-btn mpm-btn-secondary"
                  style={{ marginTop: 12 }}
                  onClick={addPicksFallbackEntry}
                >
                  + Add store
                </button>
              ) : (
                <div className="mpm-inline-note">Maximum of 10 stores reached.</div>
              )}
            </>
          );
        })()}
      </section>

      <section className="mpm-card">
        <div className="mpm-card-head">
          <div>
            <span className="mpm-label">Tier behavior</span>
            <h2>Discounts and weighting</h2>
          </div>
        </div>
        <div className="mpm-tier-grid">
          {['FREE', 'PRO', 'PREMIUM'].map((tier) => (
            <article key={tier} className="mpm-tier-card">
              <div className="mpm-tier-head">
                <strong>{tier}</strong>
                <small>{config.sponsoredProducts.defaultPackage.name}</small>
              </div>
              <label className="mpm-field">
                <span>Discount %</span>
                <input
                  type="number"
                  min="0"
                  value={tiers[tier]?.discountPercent ?? 0}
                  onChange={(e) => updateTier(tier, 'discountPercent', Number(e.target.value))}
                />
              </label>
              <label className="mpm-field">
                <span>Priority weight</span>
                <input
                  type="number"
                  min="0"
                  step="0.05"
                  value={tiers[tier]?.priorityWeight ?? 1}
                  onChange={(e) => updateTier(tier, 'priorityWeight', Number(e.target.value))}
                />
              </label>
            </article>
          ))}
        </div>
      </section>

      <section className="mpm-card">
        <div className="mpm-card-head">
          <div>
            <span className="mpm-label">Launch checklist</span>
            <h2>What still has to exist</h2>
          </div>
        </div>
        <ul className="mpm-checklist">
          <li>DNS and hosting must route <code>marketplace.{'{platformDomain}'}</code> to the public marketplace app.</li>
          <li>Public marketplace pages must read this configuration and render a customer-facing listing experience.</li>
          <li>Merchant boost checkout should use this package pricing when campaign payments are connected.</li>
        </ul>
      </section>
        </>
      )}
    </div>
  );
}

export default MarketplaceManagement;
